import { ArrowPathIcon } from '@heroicons/react/24/outline'
import { Button } from '@cottage/design-system/internal-tooling/ui'
import WarningBanner from '@/components/WarningBanner'
import { HoverHelp } from '@/tableControls'
import type {
  CloverGiftCardActivity,
  CloverSalesReport,
  GiftCardReconResult,
} from '@/services/cloverReports'
import {
  GIFT_CARD_CLASSIFICATION_OPTIONS,
  computeGiftCardDiffRows,
  effectiveClassification,
  hasProcessorReport,
  isMeaningfulGiftCardDiff,
  mapProcessorSource,
  truncateEvidenceNote,
} from '@/pages/clover/cloverGiftCardHelpers'

type Props = {
  activity: CloverGiftCardActivity | null | undefined
  recon: GiftCardReconResult | null | undefined
  salesReport: CloverSalesReport
  classifications: Record<string, string>
  canEdit: boolean
  refreshing?: boolean
  onClassificationChange: (overrideKey: string, classification: string) => void
  onRefresh: () => void
  formatMoney: (value: number | string) => string
}

function formatCtTime(timestampCt: string): string {
  const parsed = new Date(timestampCt)
  if (Number.isNaN(parsed.getTime())) return timestampCt
  return parsed.toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    timeZone: 'America/Chicago',
  })
}

function formatCardLast4(last4: string): string {
  const digits = last4.trim()
  return digits ? `…${digits}` : '…'
}

export default function CloverGiftCardsCard({
  activity,
  recon,
  salesReport,
  classifications,
  canEdit,
  refreshing = false,
  onClassificationChange,
  onRefresh,
  formatMoney,
}: Props) {
  const processorAvailable = hasProcessorReport(activity)
  const diffRows = computeGiftCardDiffRows(activity, salesReport)
  const unpaidRows = recon?.unpaid ?? []

  const showEmpty = !processorAvailable && !activity?.error

  return (
    <div className="rounded-lg bg-gray-50/90 p-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h3 className="text-sm font-semibold text-gray-900">Gift cards</h3>
        {showEmpty ? (
          <Button
            type="button"
            variant="outline"
            size="sm"
            iconOnly
            aria-label="Refresh gift card processor report"
            disabled={!canEdit || refreshing}
            onClick={onRefresh}
          >
            <ArrowPathIcon className={`h-4 w-4 ${refreshing ? 'animate-spin' : ''}`} />
          </Button>
        ) : null}
      </div>

      {activity?.error ? <WarningBanner message={activity.error} className="mt-3" /> : null}

      {showEmpty ? (
        <p className="mt-3 text-sm text-gray-600">Report not available for this day</p>
      ) : (
        <>
          <div className="mt-4 overflow-x-auto">
            <table className="table-forecast min-w-full border-separate border-spacing-0 text-sm">
              <thead>
                <tr className="text-left text-xs font-medium uppercase tracking-wide text-gray-500">
                  <th className="align-bottom font-medium" />
                  <th className="align-bottom font-medium text-right">Processor</th>
                  <th className="align-bottom font-medium text-right">Sales report</th>
                  <th className="align-bottom font-medium text-right">Diff</th>
                </tr>
              </thead>
              <tbody>
                {diffRows.map((row) => (
                  <tr key={row.key}>
                    <td className="py-1 pr-4 text-gray-600">{row.label}</td>
                    <td className="py-1 pr-4 text-right tabular-nums text-gray-900">
                      {formatMoney(row.processor)}
                    </td>
                    <td className="py-1 pr-4 text-right tabular-nums text-gray-900">
                      {formatMoney(row.sales)}
                    </td>
                    <td
                      className={`py-1 text-right tabular-nums text-gray-900 ${
                        isMeaningfulGiftCardDiff(row.diff) ? 'font-medium' : ''
                      }`}
                    >
                      {formatMoney(row.diff)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {unpaidRows.length > 0 ? (
            <div className="table-scroll-mobile mt-4 overflow-x-auto rounded-lg border border-gray-200">
              <table className="table-forecast min-w-full">
                <thead>
                  <tr className="text-left text-xs font-medium uppercase tracking-wide text-gray-500">
                    <th className="align-bottom font-medium">Time (CT)</th>
                    <th className="align-bottom font-medium">Card</th>
                    <th className="align-bottom font-medium text-right">Amount</th>
                    <th className="align-bottom font-medium">Source</th>
                    <th className="align-bottom font-medium">Evidence</th>
                    <th className="align-bottom font-medium">Classification</th>
                  </tr>
                </thead>
                <tbody>
                  {unpaidRows.map((row) => {
                    const classification = effectiveClassification(
                      row.override_key,
                      row.classification,
                      classifications,
                    )
                    const excerpt = truncateEvidenceNote(row.evidence_note)
                    const fullNote = row.evidence_note?.trim() ?? ''

                    return (
                      <tr key={row.override_key}>
                        <td className="text-sm text-gray-900">{formatCtTime(row.timestamp_ct)}</td>
                        <td className="font-mono text-sm text-gray-900">
                          {formatCardLast4(row.last4)}
                        </td>
                        <td className="text-right tabular-nums text-sm text-gray-900">
                          {formatMoney(row.amount)}
                        </td>
                        <td className="text-sm text-gray-900">{mapProcessorSource(row.source)}</td>
                        <td className="max-w-[14rem] text-sm text-gray-900">
                          {fullNote ? (
                            <HoverHelp label={excerpt || fullNote} aria-label="Order note">
                              {fullNote}
                            </HoverHelp>
                          ) : (
                            <span className="text-gray-500">no order found</span>
                          )}
                        </td>
                        <td>
                          <select
                            className="input w-full min-w-[10rem] text-sm"
                            value={classification}
                            disabled={!canEdit || refreshing}
                            onChange={(event) =>
                              onClassificationChange(row.override_key, event.target.value)
                            }
                          >
                            {GIFT_CARD_CLASSIFICATION_OPTIONS.map((option) => (
                              <option key={option.value} value={option.value}>
                                {option.label}
                              </option>
                            ))}
                          </select>
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
          ) : null}
        </>
      )}
    </div>
  )
}
